<template>
  <div
    :class="[swatchClassName, 'side-navigation-menu']"
    @click="forwardClick"
  >
    <slot />
    <div class="menu-container">
      <dx-tree-view
        ref="treeViewRef"
        :items="processedItems"
        key-expr="path"
        selection-mode="single"
        :focus-state-enabled="false"
        expand-event="click"
        @item-click="handleItemClick"
        width="100%"
      />
    </div>
  </div>
</template>

<script>
import DxTreeView from 'devextreme-vue/tree-view';
import { sizes } from '../utils/media-query';
import { onMounted, ref, watch, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { themeService } from '../theme-service';

export default {
  props: {
    compactMode: Boolean,
    items: {
      type: Array,
      required: true
    }
  },
  setup(props, context) {
    const route = useRoute();
    const router = useRouter();
    const treeViewRef = ref(null);
    const swatchClassName = ref('');

    const isLargeScreen = sizes()['screen-large'];

    const processedItems = computed(() =>
      (props.items || []).map(item => {
        const newItem = { ...item };
        if (newItem.path && !newItem.path.startsWith('/')) {
          newItem.path = '/' + newItem.path;
        }
        if (isLargeScreen) {
          newItem.expanded = true;
        }
        return newItem;
      })
    );

    function forwardClick(...args) {
      context.emit('click', args);
    }

    function handleItemClick(e) {
      if (!e.itemData.path || props.compactMode) return;
      router.push(e.itemData.path);
      e.event.stopPropagation();
    }

    function updateSelection() {
      if (!treeViewRef.value?.instance) return;
      treeViewRef.value.instance.selectItem(route.path);
      treeViewRef.value.instance.expandItem(route.path);
    }

    onMounted(() => {
      updateSelection();
      if (props.compactMode && treeViewRef.value?.instance) {
        treeViewRef.value.instance.collapseAll();
      }
    });

    watch(() => route.path, updateSelection);

    watch(
      () => themeService.isDark,
      () => {
        swatchClassName.value = themeService.isDark.value
          ? 'dx-swatch-additional-dark'
          : 'dx-swatch-additional';
      },
      { immediate: true }
    );

    watch(
      () => props.compactMode,
      () => {
        if (treeViewRef.value?.instance) {
          props.compactMode
            ? treeViewRef.value.instance.collapseAll()
            : updateSelection();
        }
      }
    );

    return {
      treeViewRef,
      processedItems,
      forwardClick,
      handleItemClick,
      swatchClassName
    };
  },
  components: {
    DxTreeView
  }
};
</script>

<style lang="scss">
@import "../variables.scss";
@import "../dx-styles.scss";

.dx-swatch-additional,
.dx-swatch-additional-dark {
  &.side-navigation-menu {
    display: flex;
    flex-direction: column;
    min-height: 100%;
    height: 100%;
    width: 250px !important;
    background-color: var(--base-bg);

    .menu-container {
      min-height: 100%;
      display: flex;
      flex: 1;

      .dx-treeview {
        white-space: nowrap;

        .dx-treeview-node-container:empty {
          display: none;
        }

        .dx-treeview-item {
          padding-left: 0;
          border-radius: 0;
          flex-direction: row-reverse;

          .dx-icon {
            width: $side-panel-min-width !important;
            margin: 0 !important;
          }
        }

        .dx-treeview-node {
          padding: 0 0 !important;
        }

        .dx-treeview-toggle-item-visibility {
          right: 10px;
          left: auto;
        }

        .dx-rtl .dx-treeview-toggle-item-visibility {
          left: 10px;
          right: auto;
        }

        .dx-treeview-node {
          &[aria-level="1"] {
            font-weight: bold;
          }

          &[aria-level="2"] .dx-treeview-item-content {
            font-weight: normal;
            padding: 0 $side-panel-min-width;
          }
        }
      }
    }
  }
}
</style>
